import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/WorkDetails.css";

import noWorkImage from "../assets/images/no_work.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MAX_DOCUMENTS = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ALLOWED_DOC_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function WorkDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [work, setWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workNotFound, setWorkNotFound] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showDescriptionFull, setShowDescriptionFull] = useState(false);

  // Quote modals
  const [showSubmitQuoteModal, setShowSubmitQuoteModal] = useState(false);
  const [showEditQuoteModal, setShowEditQuoteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [editingQuoteId, setEditingQuoteId] = useState(null);

  // Submit/Edit Quote form state
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  const [existingQuoteDocuments, setExistingQuoteDocuments] = useState([]);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [loadingEditQuote, setLoadingEditQuote] = useState(false);
  const [deletingQuoteId, setDeletingQuoteId] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [loadingDocumentId, setLoadingDocumentId] = useState(null);

  // Review modal state
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [loadingReview, setLoadingReview] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  // Map refs
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Translations
  const t = {
    en: {
      loading: "Loading",
      workDetails: "Work Details",
      basicDetails: "Basic Details",
      locationDetails: "Location Details",
      clientDetails: "Client Details",
      quoteTimeline: "Quote Timeline",
      mapView: "Map View",
      getDirections: "Get Directions",
      address: "Address",
      name: "Name",
      phone: "Phone",
      whatsapp: "Whatsapp",
      email: "Email",
      ratings: "Ratings",
      clickToKnowRatings: "(Click to know the ratings)",
      description: "Description",
      readMore: "Read More",
      readLess: "Read Less",
      documents: "Documents",
      subCategories: "Sub Categories",
      specialisations: "Specialisations",
      quoteAmount: "Quote Amount",
      rejectionNote: "Rejection Note",
      submitQuote: "Submit Quote",
      submitAnotherQuote: "Submit Another Quote",
      editQuote: "Edit Quote",
      deleteQuote: "Delete Quote",
      confirmDelete: "Confirm",
      deleteQuoteConfirm: "Are you sure you want to delete this quote?",
      reviewCustomer: "Review Customer",
      quoteSubmitted: "Quote Submitted",
      quoteApproved: "Quote Approved",
      quoteRejected: "Quote Rejected",
      pending: "Pending",
      accepted: "Accepted",
      rejected: "Rejected",
      budget: "Budget",
      maxDocumentsAllowed: "Max 5 documents allowed, upload pdf or jpg only",
      clickHereToUpload: "Click Here To Upload",
      declaration: "Declaration",
      declarationText: "I hereby declare that the information and quotation I have provided are true and accurate to the best of my knowledge. I understand that submitting false or misleading details may lead to rejection of my quote or suspension of my contractor account. I agree to comply with the terms and conditions of this platform.",
      agreeDeclaration: "I have read and agree to the above declaration.",
      cancel: "Cancel",
      submit: "Submit",
      edit: "Edit",
      delete: "Delete",
      postDate: "Post Date",
      endDate: "End Date",
      workNotFound: "Work not found",
      error: "Error",
      loadingDelete: "Deleting...",
      addReview: "Add Review",
      editReview: "Edit Review",
      yourRating: "Your Rating",
      yourReview: "Your Review",
      save: "Save",
      close: "Close",
      noWorkFound: "Work not found",
    },
    ar: {
      loading: "جاري التحميل",
      workDetails: "تفاصيل العمل",
      basicDetails: "التفاصيل الأساسية",
      locationDetails: "تفاصيل الموقع",
      clientDetails: "تفاصيل العميل",
      quoteTimeline: "الجدول الزمني للعرض",
      mapView: "عرض الخريطة",
      getDirections: "الحصول على الاتجاهات",
      address: "العنوان",
      name: "الاسم",
      phone: "الهاتف",
      whatsapp: "واتساب",
      email: "البريد الإلكتروني",
      ratings: "التقييمات",
      clickToKnowRatings: "(انقر لمعرفة التقييمات)",
      description: "الوصف",
      readMore: "اقرأ المزيد",
      readLess: "اقرأ أقل",
      documents: "المستندات",
      subCategories: "الفئات الفرعية",
      specialisations: "التخصصات",
      quoteAmount: "مبلغ العرض",
      rejectionNote: "ملاحظة الرفض",
      submitQuote: "تقديم عرض",
      submitAnotherQuote: "تقديم عرض آخر",
      editQuote: "تعديل العرض",
      deleteQuote: "حذف العرض",
      confirmDelete: "تأكيد",
      deleteQuoteConfirm: "هل أنت متأكد أنك تريد حذف هذا العرض؟",
      reviewCustomer: "مراجعة العميل",
      quoteSubmitted: "تم تقديم العرض",
      quoteApproved: "تم قبول العرض",
      quoteRejected: "تم رفض العرض",
      pending: "قيد الانتظار",
      accepted: "مقبول",
      rejected: "مرفوض",
      budget: "الميزانية",
      maxDocumentsAllowed: "الحد الأقصى 5 مستندات، قم بتحميل pdf أو jpg فقط",
      clickHereToUpload: "انقر هنا للتحميل",
      declaration: "الإقرار",
      declarationText: "أقر بأن المعلومات والعرض الذي قدمته صحيحة ودقيقة إلى أفضل علمي. أفهم أن تقديم تفاصيل كاذبة أو مضللة قد يؤدي إلى رفض عرضي أو تعليق حسابي كمقاول. أوافق على الامتثال لشروط وأحكام هذه المنصة.",
      agreeDeclaration: "لقد قرأت ووافقت على الإقرار أعلاه.",
      cancel: "إلغاء",
      submit: "إرسال",
      edit: "تعديل",
      delete: "حذف",
      postDate: "تاريخ النشر",
      endDate: "تاريخ الانتهاء",
      workNotFound: "العمل غير موجود",
      error: "خطأ",
      loadingDelete: "جاري الحذف...",
      addReview: "إضافة تقييم",
      editReview: "تعديل التقييم",
      yourRating: "تقييمك",
      yourReview: "مراجعتك",
      save: "حفظ",
      close: "إغلاق",
      noWorkFound: "العمل غير موجود",
    },
  }[lang];

  // Fetch work details
  useEffect(() => {
    const fetchWorkDetails = async () => {
      if (!id) {
        setWorkNotFound(true);
        setLoading(false);
        return;
      }

      // Validate ID format
      const idPattern = /^[a-fA-F0-9]{24}$/;
      if (!idPattern.test(id)) {
        setWorkNotFound(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setWorkNotFound(false);

        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/work/`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );

        if (data && data.response_code === 200 && data.response) {
          setWork(data.response);
          setIsFavorite(data.response.is_favorite || false);
        } else {
          setWorkNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching work details:", error);
        if (error.code === 404) {
          setWorkNotFound(true);
        } else {
          showToast(error.message || t.workNotFound, "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWorkDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, USER_BASE_URL, VERSION]);

  // Initialize map
  const initializeMap = useCallback(() => {
    if (!work?.location_coordinates || work.location_coordinates.length !== 2) {
      return;
    }

    if (!window.google || !window.google.maps || !mapRef.current) {
      return;
    }

    // location_coordinates is [longitude, latitude] format
    const lng = parseFloat(work.location_coordinates[0]);
    const lat = parseFloat(work.location_coordinates[1]);

    if (isNaN(lat) || isNaN(lng)) {
      return;
    }

    const center = { lat, lng };

    // Clear existing marker if it exists
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    // Create or update map
    let map = mapInstanceRef.current;
    if (!map) {
      map = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: 15,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });
      mapInstanceRef.current = map;
    } else {
      // Update map center if map already exists
      map.setCenter(center);
    }

    // Wait for map to be ready before adding marker
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      const marker = new window.google.maps.Marker({
        position: center,
        map: map,
        draggable: false,
        visible: true,
      });

      markerRef.current = marker;
    });
  }, [work]);

  // Load Google Maps script
  useEffect(() => {
    if (!work || !work.location_coordinates) return;

    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      initializeMap();
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [work, initializeMap]);

  // Handle favorite toggle
  const handleFavoriteToggle = async () => {
    if (!work?._id) return;

    const newFavoriteStatus = !isFavorite;
    setFavoriteLoading(true);

    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/add-update-favorites/`,
        "POST",
        JSON.stringify({
          id: work._id,
          is_favorite: newFavoriteStatus,
        }),
        { "Content-Type": "application/json" }
      );

      if (response && response.response_code === 200) {
        setIsFavorite(newFavoriteStatus);
        showToast(
          newFavoriteStatus
            ? "Added to favorites"
            : "Removed from favorites",
          "success"
        );
      }
    } catch (error) {
      console.error("Error updating favorite:", error);
      showToast(error.message || "Failed to update favorite", "error");
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${day}-${month}-${year} ${displayHours}:${minutes} ${ampm}`;
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US").format(amount);
  };

  // Get quote status badge
  const getQuoteStatusBadge = () => {
    if (!work?.latest_quote) return null;

    const status = work.latest_quote.status?.toLowerCase();
    if (status === "pending") {
      return {
        text: t.quoteSubmitted,
        bg: "#47c0c9",
        textColor: "#fff",
      };
    } else if (status === "approved") {
      return {
        text: t.quoteApproved,
        bg: "#0AC26F",
        textColor: "#fff",
      };
    } else if (status === "rejected") {
      return {
        text: t.quoteRejected,
        bg: "#FF5252",
        textColor: "#fff",
      };
    }
    return null;
  };

  // Get category name
  const getCategoryName = () => {
    if (!work) return "";
    if (work.category_id && typeof work.category_id === "object") {
      return work.category_id.name || "";
    }
    return "";
  };

  // Get category badge color based on category name
  const getCategoryBadgeColor = (categoryName) => {
    if (!categoryName) return null;
    const name = categoryName.toLowerCase();
    if (name.includes("construction")) {
      return { bg: "#47c0c9", text: "#fff" }; // Teal
    } else if (name.includes("maintenance")) {
      return { bg: "#10b981", text: "#fff" }; // Green
    } else if (name.includes("material") || name.includes("supply")) {
      return { bg: "#6b7280", text: "#fff" }; // Grey
    }
    return { bg: "#e5e7eb", text: "#374151" }; // Default grey
  };

  // Get address string
  const getAddressString = () => {
    if (!work) return "";
    const parts = [];
    if (work.address1) parts.push(work.address1);
    if (work.address2) parts.push(work.address2);
    if (work.city) parts.push(work.city);
    if (work.state) parts.push(work.state);
    if (work.pincode) parts.push(`- ${work.pincode}`);
    return parts.join(", ");
  };

  // Handle document upload
  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (!ALLOWED_DOC_TYPES.includes(file.type)) {
        invalidFiles.push(file.name);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(file.name);
        return;
      }
      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      showToast(
        `Invalid files: ${invalidFiles.join(", ")}. Max size: 5MB, allowed types: PDF, JPG, PNG, etc.`,
        "error"
      );
    }

    const currentDocs = editingQuoteId
      ? existingQuoteDocuments.length + quoteDocuments.length
      : quoteDocuments.length;

    if (currentDocs + validFiles.length > MAX_DOCUMENTS) {
      showToast(`Maximum ${MAX_DOCUMENTS} documents allowed`, "error");
      return;
    }

    setQuoteDocuments((prev) => [...prev, ...validFiles]);
  };

  // Remove document from new uploads
  const handleRemoveDocument = (index) => {
    setQuoteDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove existing document
  const handleRemoveExistingDocument = async (docId, quoteId) => {
    if (!docId || !quoteId) return;

    setDeletingDocId(docId);
    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/quote/element-delete/${quoteId}`,
        "POST",
        JSON.stringify({ key: docId }),
        { "Content-Type": "application/json" }
      );

      if (response && response.response_code === 200) {
        setExistingQuoteDocuments((prev) =>
          prev.filter((doc) => doc.id !== docId)
        );
        showToast("Document deleted successfully", "success");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast(error.message || "Failed to delete document", "error");
    } finally {
      setDeletingDocId(null);
    }
  };

  // View document
  const handleViewDocument = async (docId, workId, isQuote = false) => {
    if (!docId) {
      showToast("Document ID not found", "error");
      return;
    }

    // Set loading state
    setLoadingDocumentId(docId);

    try {
      const endpoint = isQuote
        ? `${USER_BASE_URL}/${VERSION}/work/quote/get-document/${workId}`
        : `${USER_BASE_URL}/${VERSION}/work/get-document/${workId}`;

      const response = await apiFetch(
        endpoint,
        "POST",
        JSON.stringify({ key: docId }),
        { "Content-Type": "application/json" }
      );

      if (response && response.response_code === 200) {
        // Check for both 'url' and 'signed_url' in response
        const documentUrl = response.response?.url || response.response?.signed_url;
        if (documentUrl) {
          window.open(documentUrl, "_blank");
        } else {
          showToast("Document URL not found in response", "error");
        }
      } else {
        showToast(response?.message || "Failed to load document", "error");
      }
    } catch (error) {
      console.error("Error viewing document:", error);
      showToast(error.message || "Failed to view document", "error");
    } finally {
      // Clear loading state
      setLoadingDocumentId(null);
    }
  };

  // Handle submit quote
  const handleSubmitQuote = async () => {
    const amount = parseFloat(quoteAmount);
    
    if (!quoteAmount || isNaN(amount) || amount < 1) {
      showToast("Please enter a valid quote amount", "error");
      return;
    }

    // Validate quote amount is within work amount range
    if (work?.work_amount) {
      const minAmount = work.work_amount.min || 0;
      const maxAmount = work.work_amount.max || 0;
      
      if (amount < minAmount || amount > maxAmount) {
        showToast(
          `Quote amount must be between AED ${formatAmount(minAmount)} and AED ${formatAmount(maxAmount)}`,
          "error"
        );
        return;
      }
    }

    if (quoteDocuments.length === 0 && existingQuoteDocuments.length === 0) {
      showToast("Please upload at least one document", "error");
      return;
    }

    if (!declarationChecked) {
      showToast("Please agree to the declaration", "error");
      return;
    }

    setSubmittingQuote(true);

    try {
      const formData = new FormData();
      formData.append("quote", parseFloat(quoteAmount));

      quoteDocuments.forEach((file) => {
        formData.append("file", file);
      });

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/work/quote/add/${work._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast("Quote submitted successfully", "success");
        setShowSubmitQuoteModal(false);
        setShowEditQuoteModal(false);
        resetQuoteForm();
        // Refresh work details
        window.location.reload();
      } else {
        showToast(data.message || "Failed to submit quote", "error");
      }
    } catch (error) {
      console.error("Error submitting quote:", error);
      showToast(error.message || "Failed to submit quote", "error");
    } finally {
      setSubmittingQuote(false);
    }
  };

  // Handle edit quote
  const handleEditQuote = async () => {
    if (!editingQuoteId) return;

    const amount = parseFloat(quoteAmount);
    
    if (!quoteAmount || isNaN(amount) || amount < 1) {
      showToast("Please enter a valid quote amount", "error");
      return;
    }

    // Validate quote amount is within work amount range
    if (work?.work_amount) {
      const minAmount = work.work_amount.min || 0;
      const maxAmount = work.work_amount.max || 0;
      
      if (amount < minAmount || amount > maxAmount) {
        showToast(
          `Quote amount must be between AED ${formatAmount(minAmount)} and AED ${formatAmount(maxAmount)}`,
          "error"
        );
        return;
      }
    }

    if (quoteDocuments.length === 0 && existingQuoteDocuments.length === 0) {
      showToast("Please upload at least one document", "error");
      return;
    }

    if (!declarationChecked) {
      showToast("Please agree to the declaration", "error");
      return;
    }

    setSubmittingQuote(true);

    try {
      const formData = new FormData();
      formData.append("quote", parseFloat(quoteAmount));

      quoteDocuments.forEach((file) => {
        formData.append("file", file);
      });

      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/work/quote/edit/${editingQuoteId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast("Quote updated successfully", "success");
        setShowEditQuoteModal(false);
        resetQuoteForm();
        // Refresh work details
        window.location.reload();
      } else {
        showToast(data.message || "Failed to update quote", "error");
      }
    } catch (error) {
      console.error("Error updating quote:", error);
      showToast(error.message || "Failed to update quote", "error");
    } finally {
      setSubmittingQuote(false);
    }
  };

  // Handle delete quote - show confirmation first
  const handleDeleteQuote = (quoteId) => {
    if (!quoteId) return;
    setQuoteToDelete(quoteId);
    setShowDeleteConfirmModal(true);
  };

  // Confirm and perform delete quote
  const confirmDeleteQuote = async () => {
    if (!quoteToDelete) return;

    setDeletingQuoteId(quoteToDelete);
    setShowDeleteConfirmModal(false);
    
    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/quote/delete/${quoteToDelete}`,
        "GET",
        null,
        {}
      );

      if (response && response.response_code === 200) {
        showToast("Quote deleted successfully", "success");
        // Refresh work details
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting quote:", error);
      showToast(error.message || "Failed to delete quote", "error");
    } finally {
      setDeletingQuoteId(null);
      setQuoteToDelete(null);
    }
  };

  // Cancel delete quote
  const cancelDeleteQuote = () => {
    setShowDeleteConfirmModal(false);
    setQuoteToDelete(null);
  };

  // Open edit quote modal
  const handleOpenEditQuote = async (quoteId) => {
    setEditingQuoteId(quoteId);
    setShowEditQuoteModal(true);
    setLoadingEditQuote(true);

    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/quote/${quoteId}`,
        "GET",
        null,
        {}
      );

      if (response && response.response_code === 200 && response.response) {
        const quote = response.response;
        setQuoteAmount(quote.quote?.toString() || "");
        setExistingQuoteDocuments(quote.documents || []);
        setQuoteDocuments([]);
        setDeclarationChecked(true);
      } else {
        showToast("Failed to load quote details", "error");
        setShowEditQuoteModal(false);
      }
    } catch (error) {
      console.error("Error fetching quote:", error);
      showToast(error.message || "Failed to load quote details", "error");
      setShowEditQuoteModal(false);
    } finally {
      setLoadingEditQuote(false);
    }
  };

  // Reset quote form
  const resetQuoteForm = () => {
    setQuoteAmount("");
    setQuoteDocuments([]);
    setExistingQuoteDocuments([]);
    setDeclarationChecked(false);
    setEditingQuoteId(null);
  };

  // Open submit quote modal
  const handleOpenSubmitQuote = () => {
    resetQuoteForm();
    setShowSubmitQuoteModal(true);
  };

  // Close quote modals
  const handleCloseQuoteModals = () => {
    setShowSubmitQuoteModal(false);
    setShowEditQuoteModal(false);
    resetQuoteForm();
  };

  // Fetch review
  const fetchReview = async (userId) => {
    if (!userId) return;

    setLoadingReview(true);
    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/fetch-review/`,
        "POST",
        JSON.stringify({ id: userId }),
        { "Content-Type": "application/json" }
      );

      if (response && response.response_code === 200 && response.response) {
        setReviewStars(response.response.star || 0);
        setReviewText(response.response.review || "");
      } else {
        // No existing review, reset to defaults
        setReviewStars(0);
        setReviewText("");
      }
    } catch (error) {
      console.error("Error fetching review:", error);
      // If no review exists, that's okay - reset to defaults
      setReviewStars(0);
      setReviewText("");
    } finally {
      setLoadingReview(false);
    }
  };

  // Open review modal
  const handleOpenReviewModal = () => {
    if (work?.user_id?._id) {
      fetchReview(work.user_id._id);
    }
    setShowReviewModal(true);
  };

  // Submit review
  const handleSubmitReview = async () => {
    if (!work?.user_id?._id) return;

    if (reviewStars < 1 || reviewStars > 5) {
      showToast("Please select a rating", "error");
      return;
    }

    if (!reviewText.trim() || reviewText.trim().length < 1) {
      showToast("Please enter a review", "error");
      return;
    }

    if (reviewText.trim().length > 1000) {
      showToast("Review must be less than 1000 characters", "error");
      return;
    }

    setSubmittingReview(true);

    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/add-update-review/`,
        "POST",
        JSON.stringify({
          user_id: work.user_id._id,
          star: reviewStars,
          review: reviewText.trim(),
        }),
        { "Content-Type": "application/json" }
      );

      if (response && response.response_code === 200) {
        showToast("Review submitted successfully", "success");
        setShowReviewModal(false);
        setReviewStars(0);
        setReviewText("");
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      showToast(error.message || "Failed to submit review", "error");
    } finally {
      setSubmittingReview(false);
    }
  };

  // Check if should show submit quote button
  const shouldShowSubmitQuoteButton = () => {
    if (!work) return false;

    // If work is completed and contractor has submitted at least one quote, show review button instead
    if (work.status === "completed" && work.quotes && work.quotes.length > 0) {
      return false;
    }

    // If latest quote is approved, don't show submit button
    if (work.latest_quote?.status === "approved") {
      return false;
    }

    // If latest quote is pending, don't show submit button
    if (work.latest_quote?.status === "pending") {
      return false;
    }

    // If no quotes or last quote is rejected, show submit button
    return true;
  };

  // Check if should show review button
  const shouldShowReviewButton = () => {
    if (!work) return false;
    return (
      work.status === "completed" &&
      work.quotes &&
      work.quotes.length > 0 &&
      work.latest_quote?.status === "approved"
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="work-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="work-details-loading">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
        <BottomNavbar currentPage="home" lang={lang} />
      </div>
    );
  }

  // Work not found
  if (workNotFound || !work) {
    return (
      <div className="work-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="work-details-page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i>
          </button>
          <h1 className="work-details-page-title">{t.workDetails}</h1>
          <div className="header-spacer"></div>
        </div>
        <div
          className="work-details-content"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
            gap: "16px",
            textAlign: "center",
          }}
        >
          <img
            src={noWorkImage}
            alt="No work"
            style={{ maxWidth: "300px", width: "100%" }}
          />
          <p style={{ fontSize: "16px", color: "#6b7280", margin: 0 }}>
            {t.noWorkFound}
          </p>
        </div>
        <BottomNavbar currentPage="home" lang={lang} />
      </div>
    );
  }

  const quoteStatusBadge = getQuoteStatusBadge();
  const addressString = getAddressString();
  const categoryName = getCategoryName();
  const categoryBadgeColor = getCategoryBadgeColor(categoryName);

  return (
    <div className="work-details-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header lang={lang} />

      {/* Page Header */}
      <div className="work-details-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="work-details-page-title">{t.workDetails}</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="work-details-content">
        {/* Basic Details */}
        <div className="work-details-section">
          <div className="work-details-section-title-container">
            <h2 className="work-details-section-title">{t.basicDetails}</h2>
            {quoteStatusBadge && (
              <span
                className="work-status-badge"
                style={{
                  backgroundColor: quoteStatusBadge.bg,
                  color: quoteStatusBadge.textColor,
                }}
              >
                {quoteStatusBadge.text}
              </span>
            )}
          </div>
          <div className="work-details-card">
            <div className="work-details-card-header">
              <div className="work-details-title-container">
                <h3 className="work-category-title">
                  {work?.title || work?.category_id?.name || "Construction"}
                </h3>
                {work?.title && categoryName && categoryBadgeColor && (
                  <span
                    className="work-details-category-badge"
                    style={{
                      backgroundColor: categoryBadgeColor.bg,
                      color: categoryBadgeColor.text,
                    }}
                  >
                    {categoryName}
                  </span>
                )}
              </div>
              <button
                className={`work-favorite-btn ${isFavorite ? "active" : ""}`}
                onClick={handleFavoriteToggle}
                disabled={favoriteLoading}
              >
                <i
                  className={`bx ${isFavorite ? "bxs-heart" : "bx-heart"}`}
                ></i>
              </button>
            </div>

            <div className="work-info-item">
              <i className="bx bx-map"></i>
              <span>{addressString}</span>
            </div>

            <div className="work-info-item">
              <i className="bx bx-calendar"></i>
              <span>
                <b>{t.postDate}:</b> {formatDate(work.created_at)}
                {work.quote_last_date && (
                  <>
                    {" "}&nbsp;&nbsp;
                    <b>{t.endDate}:</b> {formatDate(work.quote_last_date)}
                  </>
                )}
              </span>
            </div>

            {work.work_amount && (
              <div className="work-info-item">
                <i className="bx bx-money"></i>
                <span>
                  AED {formatAmount(work.work_amount.min || 0)} - AED{" "}
                  {formatAmount(work.work_amount.max || 0)}
                </span>
              </div>
            )}

            {work.description && (
              <div className="work-description-section">
                <h4 className="work-subsection-title">{t.description}</h4>
                <p className={`work-description-text ${showDescriptionFull ? 'expanded' : ''}`}>
                  {(() => {
                    if (work.description.length <= 150) {
                      return work.description;
                    }
                    if (showDescriptionFull) {
                      return work.description;
                    }
                    return `${work.description.substring(0, 150)}...`;
                  })()}
                  {work.description.length > 150 && (
                    <>
                      {" "}
                      <button
                        className="read-more-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDescriptionFull((prev) => !prev);
                        }}
                        type="button"
                      >
                        {showDescriptionFull ? t.readLess : t.readMore}
                      </button>
                    </>
                  )}
                </p>
              </div>
            )}

            {work.documents && work.documents.length > 0 && (
              <div className="work-documents-section">
                <h4 className="work-subsection-title">{t.documents}</h4>
                <div className="work-documents-list">
                  {work.documents.map((doc, index) => {
                    const isLoading = loadingDocumentId === doc.id;
                    return (
                      <div
                        key={doc.id || index}
                        className={`work-document-item ${isLoading ? "loading" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (doc.id && !isLoading) {
                            handleViewDocument(doc.id, work._id, false);
                          } else if (!doc.id) {
                            showToast("Document ID not available", "error");
                          }
                        }}
                      >
                        {isLoading ? (
                          <div className="document-loading-spinner">
                            <div className="spinner-small"></div>
                          </div>
                        ) : (
                          <div className="document-icon">PDF</div>
                        )}
                        <span className="document-name">
                          {doc.id?.split("/").pop()?.substring(0, 15) || "Document"}
                          ...
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {work.sub_categories && work.sub_categories.length > 0 && (
              <div className="work-subcategories-section">
                <h4 className="work-subsection-title">{t.subCategories}</h4>
                <div className="work-subcategories-list">
                  {work.sub_categories.map((subCat, index) => (
                    <span key={subCat._id || index} className="subcategory-chip">
                      {subCat.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {work.specialisations && work.specialisations.length > 0 && (
              <div className="work-subcategories-section">
                <h4 className="work-subsection-title">{t.specialisations}</h4>
                <div className="work-subcategories-list">
                  {work.specialisations.map((spec, index) => (
                    <span key={spec._id || index} className="subcategory-chip">
                      {typeof spec === "object" ? spec.name : spec}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location Details */}
        {work.location_coordinates && work.location_coordinates.length === 2 && (
          <div className="work-details-section">
            <h2 className="work-details-section-title">{t.locationDetails}</h2>
            <div className="work-details-card">
              <h4 className="work-subsection-title">{t.mapView}</h4>
              <div ref={mapRef} className="work-map-container"></div>
              {work.location_coordinates && work.location_coordinates.length === 2 && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${work.location_coordinates[1]},${work.location_coordinates[0]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="get-directions-link"
                >
                  <i className="bx bx-navigation"></i>
                  {t.getDirections}
                </a>
              )}
              <h4 className="work-subsection-title" style={{ marginTop: "16px" }}>
                {t.address}
              </h4>
              <p className="work-address-text">{addressString}</p>
            </div>
          </div>
        )}

        {/* Client Details */}
        {work.user_id && (
          <div className="work-details-section">
            <h2 className="work-details-section-title">{t.clientDetails}</h2>
            <div className="work-details-card">
              <div className="client-info-item">
                <h4 className="work-subsection-title">{t.name}</h4>
                <p className="client-info-text">{work.user_id.name || "N/A"}</p>
              </div>

              {work.user_id.phone && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.phone}</h4>
                  <a 
                    href={`tel:${work.user_id.phone}`}
                    className="client-info-link"
                  >
                    {work.user_id.phone}
                  </a>
                </div>
              )}

              {work.user_id.whatsapp_number && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.whatsapp}</h4>
                  <a 
                    href={`https://wa.me/${work.user_id.whatsapp_number.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="client-info-link"
                  >
                    {work.user_id.whatsapp_number}
                  </a>
                </div>
              )}

              {work.user_id.email && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.email}</h4>
                  <a 
                    href={`mailto:${work.user_id.email}`}
                    className="client-info-link"
                  >
                    {work.user_id.email}
                  </a>
                </div>
              )}

              {work.user_id.average_rating !== undefined && (
                <div className="client-info-item">
                  <h4 className="work-subsection-title">{t.ratings}</h4>
                  <div className="rating-stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`bx ${
                          star <= Math.floor(work.user_id.average_rating)
                            ? "bxs-star"
                            : star <= work.user_id.average_rating
                            ? "bxs-star-half"
                            : "bx-star"
                        }`}
                        style={{ color: "#f59e0b" }}
                      ></i>
                    ))}
                  </div>
                  <p className="rating-hint">
                    {work.user_id.average_rating?.toFixed(1) || "0.0"} / 5.0
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quote Timeline */}
        {work.quotes && work.quotes.length > 0 && (
          <div className="work-details-section">
            <h2 className="work-details-section-title">{t.quoteTimeline}</h2>
            {work.latest_quote?.status === "approved" ? (
              // Show only latest approved quote
              <div className="work-details-card">
                <div className="quote-timeline-item" style={{ borderBottom: 'none', padding: '0' }}>
                  <div className="quote-timeline-header">
                    <span className="quote-date">
                      {formatDateTime(work.latest_quote.created_at)}
                    </span>
                    <span
                      className="quote-status-badge approved"
                      style={{ backgroundColor: "#0AC26F", color: "#fff" }}
                    >
                      {t.accepted}
                    </span>
                  </div>
                  <div className="quote-timeline-content">
                    <div className="quote-info-row">
                      <h4 className="quote-info-label">{t.quoteAmount}</h4>
                      <p className="quote-info-value">
                        AED {formatAmount(work.latest_quote.quote || 0)}
                      </p>
                    </div>
                    {work.latest_quote.documents &&
                      work.latest_quote.documents.length > 0 && (
                        <div className="quote-info-row">
                          <h4 className="quote-info-label">{t.documents}</h4>
                          <div className="quote-documents-list">
                            {work.latest_quote.documents.map((doc, index) => {
                              const isLoading = loadingDocumentId === doc.id;
                              return (
                                <div
                                  key={doc.id || index}
                                  className={`quote-document-item ${isLoading ? "loading" : ""}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (doc.id && !isLoading) {
                                      handleViewDocument(
                                        doc.id,
                                        work.latest_quote._id,
                                        true
                                      );
                                    }
                                  }}
                                >
                                  {isLoading ? (
                                    <div className="small-document-loading-spinner">
                                      <div className="spinner-small"></div>
                                    </div>
                                  ) : (
                                    <div className="small-document-icon">PDF</div>
                                  )}
                                  <span className="small-document-name">
                                    {doc.id?.split("/").pop()?.substring(0, 15) ||
                                      "Layout"}
                                    ...
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            ) : (
              // Show all quotes as separate cards
              <div className="quotes-cards-container">
                {work.quotes.map((quote, index) => (
                  <div key={quote._id || index} className="work-details-card">
                    <div className="quote-timeline-item" style={{ borderBottom: 'none', padding: '0' }}>
                    <div className="quote-timeline-header">
                      <span className="quote-date">
                        {formatDateTime(quote.created_at)}
                      </span>
                      <span
                        className={`quote-status-badge ${quote.status?.toLowerCase()}`}
                        style={{
                          backgroundColor:
                            quote.status === "pending"
                              ? "#47c0c9"
                              : quote.status === "approved"
                              ? "#0AC26F"
                              : "#FF5252",
                          color: "#fff",
                        }}
                      >
                        {quote.status === "pending"
                          ? t.pending
                          : quote.status === "approved"
                          ? t.accepted
                          : t.rejected}
                      </span>
                    </div>
                    <div className="quote-timeline-content">
                      <div className="quote-info-row">
                        <h4 className="quote-info-label">{t.quoteAmount}</h4>
                        <p className="quote-info-value">
                          AED {formatAmount(quote.quote || 0)}
                        </p>
                      </div>
                      {quote.documents && quote.documents.length > 0 && (
                        <div className="quote-info-row">
                          <h4 className="quote-info-label">{t.documents}</h4>
                          <div className="quote-documents-list">
                            {quote.documents.map((doc, docIndex) => {
                              const isLoading = loadingDocumentId === doc.id;
                              return (
                                <div
                                  key={doc.id || docIndex}
                                  className={`quote-document-item ${isLoading ? "loading" : ""}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (doc.id && !isLoading) {
                                      handleViewDocument(doc.id, quote._id, true);
                                    }
                                  }}
                                >
                                  {isLoading ? (
                                    <div className="small-document-loading-spinner">
                                      <div className="spinner-small"></div>
                                    </div>
                                  ) : (
                                    <div className="small-document-icon">PDF</div>
                                  )}
                                  <span className="small-document-name">
                                    {doc.id?.split("/").pop()?.substring(0, 15) ||
                                      "Layout"}
                                    ...
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {quote.rejection_note && (
                        <div className="quote-info-row">
                          <h4 className="quote-info-label">{t.rejectionNote}</h4>
                          <p className="quote-rejection-note">
                            {quote.rejection_note}
                          </p>
                        </div>
                      )}
                      {quote.status === "pending" && (
                        <div className="quote-actions">
                          <button
                            className="quote-action-btn delete"
                            onClick={() => handleDeleteQuote(quote._id)}
                            disabled={deletingQuoteId === quote._id}
                          >
                            {deletingQuoteId === quote._id
                              ? t.loadingDelete
                              : t.deleteQuote}
                          </button>
                          <button
                            className="quote-action-btn edit"
                            onClick={() => handleOpenEditQuote(quote._id)}
                          >
                            {t.editQuote} &gt;
                          </button>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Button */}
        {shouldShowReviewButton() && (
          <button
            className="work-action-button"
            onClick={handleOpenReviewModal}
          >
            {t.reviewCustomer}
          </button>
        )}

        {shouldShowSubmitQuoteButton() && (
          <button
            className="work-action-button"
            onClick={handleOpenSubmitQuote}
          >
            {work.quotes && work.quotes.length > 0
              ? t.submitAnotherQuote
              : t.submitQuote}
          </button>
        )}
      </div>

      {/* Submit Quote Modal */}
      {(showSubmitQuoteModal || showEditQuoteModal) && (
        <div
          className="quote-modal-overlay"
          onClick={handleCloseQuoteModals}
        >
          <div
            className="quote-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quote-modal-header">
              <h2 className="quote-modal-title">
                {showEditQuoteModal ? t.editQuote : t.submitQuote}
              </h2>
              <button
                className="quote-modal-close"
                onClick={handleCloseQuoteModals}
                disabled={loadingEditQuote}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="quote-modal-body" style={{ position: 'relative' }}>
              {loadingEditQuote && (
                <div className="quote-modal-loading-overlay">
                  <div className="spinner"></div>
                  <p>{t.loading}</p>
                </div>
              )}
              <div className="quote-form-group">
                <label className="quote-form-label">{t.budget}</label>
                <input
                  type="number"
                  className="quote-form-input"
                  value={quoteAmount}
                  onChange={(e) => setQuoteAmount(e.target.value)}
                  placeholder={`Enter amount (AED ${work?.work_amount?.min ? formatAmount(work.work_amount.min) : '0'} - AED ${work?.work_amount?.max ? formatAmount(work.work_amount.max) : '0'})`}
                  min={work?.work_amount?.min || 1}
                  max={work?.work_amount?.max || 10000000}
                />
                {work?.work_amount && (
                  <p className="quote-form-hint" style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                    Amount must be between AED {formatAmount(work.work_amount.min)} and AED {formatAmount(work.work_amount.max)}
                  </p>
                )}
              </div>

              <div className="quote-form-group">
                <label className="quote-form-label">{t.documents}</label>
                <p className="quote-form-hint">{t.maxDocumentsAllowed}</p>
                <div
                  className="quote-upload-area"
                  onClick={() =>
                    document.getElementById("quote-file-input")?.click()
                  }
                >
                  <i className="bx bx-cloud-upload"></i>
                  <p>{t.clickHereToUpload}</p>
                </div>
                <input
                  id="quote-file-input"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.svg,.webp,.avif,.heic,.heif,.doc,.docx"
                  onChange={handleDocumentUpload}
                  style={{ display: "none" }}
                />

                {/* Existing Documents */}
                {existingQuoteDocuments.length > 0 && (
                  <div className="quote-documents-preview">
                    {existingQuoteDocuments.map((doc, index) => (
                      <div key={doc.id || index} className="quote-document-preview">
                        <div className="document-preview-icon">PDF</div>
                        <span className="document-preview-name">
                          {doc.id?.split("/").pop()?.substring(0, 15) || "Document"}
                          ...
                        </span>
                        <button
                          className="document-remove-btn"
                          onClick={() =>
                            handleRemoveExistingDocument(doc.id, editingQuoteId)
                          }
                          disabled={deletingDocId === doc.id}
                        >
                          {deletingDocId === doc.id ? (
                            <div className="spinner-small"></div>
                          ) : (
                            <i className="bx bx-x"></i>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Documents */}
                {quoteDocuments.length > 0 && (
                  <div className="quote-documents-preview">
                    {quoteDocuments.map((file, index) => (
                      <div key={index} className="quote-document-preview">
                        <div className="document-preview-icon">PDF</div>
                        <span className="document-preview-name">
                          {file.name.substring(0, 15)}...
                        </span>
                        <button
                          className="document-remove-btn"
                          onClick={() => handleRemoveDocument(index)}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="quote-form-group">
                <h4 className="quote-declaration-title">{t.declaration}</h4>
                <p className="quote-declaration-text">{t.declarationText}</p>
                <label className="quote-checkbox-label">
                  <input
                    type="checkbox"
                    checked={declarationChecked}
                    onChange={(e) => setDeclarationChecked(e.target.checked)}
                  />
                  <span>{t.agreeDeclaration}</span>
                </label>
              </div>
            </div>

            <div className="quote-modal-footer">
              <button
                className="quote-modal-btn cancel"
                onClick={handleCloseQuoteModals}
                disabled={loadingEditQuote}
              >
                {t.cancel}
              </button>
              <button
                className="quote-modal-btn submit"
                onClick={
                  showEditQuoteModal ? handleEditQuote : handleSubmitQuote
                }
                disabled={submittingQuote || loadingEditQuote}
              >
                {submittingQuote ? t.loading : t.submit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div
          className="review-modal-overlay"
          onClick={() => setShowReviewModal(false)}
        >
          <div
            className="review-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="review-modal-header">
              <h2 className="review-modal-title">
                {reviewStars > 0 || reviewText ? t.editReview : t.addReview}
              </h2>
              <button
                className="review-modal-close"
                onClick={() => setShowReviewModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="review-modal-body">
              {loadingReview ? (
                <div className="review-loading">
                  <div className="spinner"></div>
                  <p>{t.loading}</p>
                </div>
              ) : (
                <>
                  <div className="review-form-group">
                    <label className="review-form-label">{t.yourRating}</label>
                    <div className="review-stars-input">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          className={`review-star-btn ${
                            star <= reviewStars ? "active" : ""
                          }`}
                          onClick={() => setReviewStars(star)}
                        >
                          <i className="bx bxs-star"></i>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="review-form-group">
                    <label className="review-form-label">{t.yourReview}</label>
                    <textarea
                      className="review-form-textarea"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Enter your review..."
                      rows="5"
                      maxLength="1000"
                    />
                    <p className="review-char-count">
                      {reviewText.length}/1000
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="review-modal-footer">
              <button
                className="review-modal-btn cancel"
                onClick={() => setShowReviewModal(false)}
              >
                {t.close}
              </button>
              <button
                className="review-modal-btn submit"
                onClick={handleSubmitReview}
                disabled={submittingReview || loadingReview}
              >
                {submittingReview ? t.loading : t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div
          className="delete-confirm-modal-overlay"
          onClick={cancelDeleteQuote}
        >
          <div
            className="delete-confirm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-confirm-modal-header">
              <h3 className="delete-confirm-modal-title">{t.confirmDelete}</h3>
            </div>
            <div className="delete-confirm-modal-body">
              <p className="delete-confirm-modal-message">
                {t.deleteQuoteConfirm}
              </p>
            </div>
            <div className="delete-confirm-modal-footer">
              <button
                className="delete-confirm-modal-btn cancel"
                onClick={cancelDeleteQuote}
                disabled={deletingQuoteId === quoteToDelete}
              >
                {t.cancel}
              </button>
              <button
                className="delete-confirm-modal-btn confirm"
                onClick={confirmDeleteQuote}
                disabled={deletingQuoteId === quoteToDelete}
              >
                {deletingQuoteId === quoteToDelete ? t.loadingDelete : t.confirmDelete}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavbar currentPage="home" lang={lang} />
    </div>
  );
}

